import { Component, ViewChild, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService, Logger } from '@se/se.ui.html5';
import {
    ExternalDocSearchInfo,
    HiararchicalExternalDocument,
    ExternalDocument,
    ExternalDocPatientInfo,
} from '../models/estensa-models.model';
import { BehaviorSubject, Subscription, Subject } from 'rxjs';
import { ExternalDocsService } from './external-docs.service';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { RootState } from '../root-store/root.state';
import {
    seOpenExternalDocsOnDialog,
    seSetSelectExternalDoc,
    seUpdateSelectExternalDoc,
    seAddNewExternalDoc,
} from './redux/external-docs.actions';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { ExternalDocsState } from './redux/external-docs.state';
import { UploadingMode, DocFamilyType as ExternalDocFamilyType } from 'estensa-module';
import { arrayBufferToBase64, convertBase64ToUint8Array } from '../utils/utils';
import { AutoUnsubscribe } from '../decorators/auto-unsubscribe';

@Component({
    selector: 'se-external-docs-dialog',
    templateUrl: './se-external-docs-dialog.component.html',
    styleUrls: ['./se-external-docs-dialog.component.scss'],
})
export class SeExternalDocsDialogComponent implements OnDestroy, OnInit {

    @ViewChild('seExternalDocsDialogTemplate')
    seExternalDocsDialogTemplate: TemplateRef<any>;

    @ViewChild('imageDocViewer')
    imageDocViewer: TemplateRef<any>;

    private seExternalDocsDialog: MatDialogRef<any>;
    private currentExternalDocSearchInfo: ExternalDocSearchInfo;
    private currentExternalDocPatientInfo: ExternalDocPatientInfo;
    private currentExternalDocExamInfo: string[];

    private _isWaiting = new BehaviorSubject<boolean>(false);
    get isWaiting(): BehaviorSubject<boolean> {
        return this._isWaiting;
    }

    private _isOnServerOp = new BehaviorSubject<boolean>(false);
    get isOnServerOp(): BehaviorSubject<boolean> {
        return this._isOnServerOp;
    }

    get isCloseButtonDisabled(): boolean {
        return this._isWaiting.value || this.isOnError || this.isAddingDoc ||
            this.isDeletingDoc || this.isEditingDoc || this.isLoadingDoc || this._isOnServerOp.value;
    }

    pdfSource: Uint8Array;
    imageSource;

    isOnError: boolean = false;
    isDeletingDoc: boolean = false;
    isAddingDoc: boolean = false;
    isEditingDoc: boolean = false;
    isLoadingDoc: boolean = false;
    isConfirmedOp: boolean = false;

    messageBoxText: string = '';

    private loadSubscription: Subscription;
    private updateDocSubscription: Subscription;
    private addNewDocSubscription: Subscription;
    private deleteDocSubscription: Subscription;
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    private afterCloseEventSubscription: Subscription;
    private afterOpenEventSubscription: Subscription;

    externalDocuments: HiararchicalExternalDocument[] = [];
    _selectedDoc: ExternalDocument;
    _selectedDocType?: ExternalDocFamilyType;

    zoomPdf = 1;
    zoomImg = 100;

    // constructor

    constructor(public dialog: MatDialog,
                private externalDocsService: ExternalDocsService,
                private actions: Actions,
                private store: Store<RootState>,
                private notificationService: NotificationService,
                private translate: TranslateService,
                private logger: Logger,
    ) {
    }

    public ngOnInit() {
        this.listenForStoreEvents();
    }

    @AutoUnsubscribe()
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public getEmptyMessage(): string {
        let areDocsPresent = this.externalDocuments.some(
            externalDocument => (!!externalDocument.linkedExternalDocuments && externalDocument.linkedExternalDocuments.length > 0));
        if (areDocsPresent)
            return this.translate.instant('ExtDoc_NoDocSelected');


        return this.translate.instant('ExtDoc_NoDocPresent');
    }

    public closeModal() {
        if (!this.isCloseButtonDisabled)
            this.seExternalDocsDialog.close(true);
    }

    public onEditRequired(document: ExternalDocument) {
        this._selectedDocType = this.getDocumentType(document);
        this._selectedDoc = document;

        this.isEditingDoc = true;
    }

    public onAddRequired(document: ExternalDocument) {
        const externalDoc: ExternalDocument = document;

        // Completo le info paziente
        externalDoc.guidPatient = this.currentExternalDocPatientInfo.guidPatient;
        externalDoc.patientName = this.currentExternalDocPatientInfo.patientName;
        externalDoc.patientSurname = this.currentExternalDocPatientInfo.patientSurname;
        externalDoc.patientSex = this.currentExternalDocPatientInfo.patientSex;
        externalDoc.patientID = this.currentExternalDocPatientInfo.patientID;

        // date
        externalDoc.creationDateTime = new Date(Date.now());
        externalDoc.eventDateTime = new Date(Date.now());


        this._selectedDocType = (externalDoc.mode == UploadingMode.Patient) ? ExternalDocFamilyType.Patient : ExternalDocFamilyType.Study;
        this._selectedDoc = externalDoc;


        this.isAddingDoc = true;
    }

    public onDeleteRequired(document: ExternalDocument) {
        this._selectedDocType = this.getDocumentType(document);
        this._selectedDoc = document;

        this.store.dispatch(seSetSelectExternalDoc({
            doc: document,
        }));

        this.messageBoxText = this.translate.instant('AreYouSureToDelete');
        this.isDeletingDoc = true;
    }

    public cancelOps(_) {
        this.resetFlags();
        this.messageBoxText = '';
    }

    public confirmOps(ops) {

        this.isOnError = false;
        this.messageBoxText = '';

        if (ops == 'adding') {
            this.isAddingDoc = false;
            this.addDocument();
        } else if (ops == 'modify') {
            this.isEditingDoc = false;
            this.updateDocument();
        } else if (ops == 'delete') {
            this.isDeletingDoc = false;
            this.deleteDocument();
        } else { // closeMessageBox
            this.resetFlags();
        }
    }

    public zoomIn() {
        if (this.zoomPdf < 1.3)
            this.zoomPdf += 0.1;
    }

    public zoomOut() {
        if (this.zoomPdf > 0.6) {
            this.zoomPdf -= 0.1;
        }
    }

    public zoomImgIn() {
        if (this.zoomPdf < 130)
            this.zoomImg += 10;
    }

    public zoomImgOut() {
        if (this.zoomImg > 60)
            this.zoomImg -= 10;

    }

    public canShowPdfViewer() {
        // 1 - PDF, 2 - JPEG, 3 - PNG
        return this._selectedDoc && this._selectedDoc.fileType == 1 &&
            !this.isLoadingDoc && !this.isAddingDoc /* && !this.isEditingDoc && !this.isDeletingDoc */ && !this.isOnError;
    }

    public canShowImageViewer() {
        // 1 - PDF, 2 - JPEG, 3 - PNG
        return this._selectedDoc && this._selectedDoc.fileType > 1 &&
            !this.isLoadingDoc && !this.isAddingDoc /* && !this.isEditingDoc && !this.isDeletingDoc */ && !this.isOnError;
    }

    private loadExternalDocuments() {

        this.loadSubscription = this.externalDocsService.loadAllExternalDocs(this.currentExternalDocSearchInfo)
            .pipe(finalize(() => this._isWaiting.next(false)))
            .subscribe({
                next: (docs: HiararchicalExternalDocument[]) => {
                    /* faccio il toUpper dei uid altrimenti rischio...*/
                    docs.forEach(doc => doc.linkedExternalDocuments.forEach((d: ExternalDocument) => d.docType = d.docType.toUpperCase()));
                    /* Il server mi ritorna solo gli item con documenti allegati, ma a me, per poterli poi aggiungere, mi serve creare al volo anche quelli che mancano*/
                    this.externalDocuments = docs;
                },
                error: (error) => {
                    // Notifica errore;
                    let title = this.translate.instant('Exception_ExternalDocsFault');
                    let defaultMessage = this.translate.instant('CannotLoadExternalDocs');
                    this.notificationService.httpEstensaFaultError(error, title, defaultMessage);
                    this.logger.error(error);

                    this.messageBoxText = defaultMessage;
                    this.isOnError = true;
                    this.externalDocuments = [];
                }
            });
    }

    private loadSelectedDoc() {
        this.zoomImg = 100;
        this.zoomPdf = 1;

        // Se il byte array non è presente lo scarico
        if (!this._selectedDoc.docBase64ByteArray) {
            this.externalDocsService.getExternalDoc(this._selectedDoc)
                .subscribe({
                    // Success.
                    next: (src) => {
                        const _docBase64ByteArray = arrayBufferToBase64(src);
                        if (_docBase64ByteArray) {
                            /* Chaching */
                            const _tmpDoc = JSON.parse(JSON.stringify(this._selectedDoc));
                            _tmpDoc.docBase64ByteArray = _docBase64ByteArray;
                            this.updateDocumentInCollection(_tmpDoc);
                            this._selectedDoc = _tmpDoc;

                            if (this._selectedDoc.fileType == 1) {
                                this.pdfSource = convertBase64ToUint8Array(_docBase64ByteArray);
                            } else if (this._selectedDoc.fileType == 2)
                                this.imageSource = 'data:image/jpeg;base64,' + _docBase64ByteArray;
                            else if (this._selectedDoc.fileType == 3)
                                this.imageSource = 'data:image/png;base64,' + _docBase64ByteArray;

                            setTimeout(() => { // ritardo un po pe rdare il tempo di visualizzare
                                this.isLoadingDoc = false;
                            }, 1000);
                        } else {
                            this.messageBoxText = this.translate.instant('CannotLoadExternalDocs');

                            /* Annullo selezione */
                            this.store.dispatch(seSetSelectExternalDoc({
                                doc: null,
                            }));

                            this.isOnError = true;
                        }
                    },
                    // Error.
                    error: (error) => {
                        let title = this.translate.instant('Exception_ExternalDocsFault');
                        let defaultMessage = this.translate.instant('CannotLoadExternalDocs');

                        this.notificationService.httpEstensaFaultError(error, title, defaultMessage);
                        this.logger.error(error);

                        this.messageBoxText = defaultMessage;

                        /* Annullo selezione */
                        this.store.dispatch(seSetSelectExternalDoc({
                            doc: null,
                        }));

                        /* Qui va messo con il redux la selection a null altrimenti il pezzo con il tree non ci capisce piu' niente */
                        this.isOnError = true;
                    },
                });
        } else {
            if (this._selectedDoc.fileType == 1) {
                this.pdfSource = convertBase64ToUint8Array(this._selectedDoc.docBase64ByteArray);
            } else if (this._selectedDoc.fileType == 2)
                this.imageSource = 'data:image/jpeg;base64,' + this._selectedDoc.docBase64ByteArray;
            else if (this._selectedDoc.fileType == 3)
                this.imageSource = 'data:image/png;base64,' + this._selectedDoc.docBase64ByteArray;

            setTimeout(() => { // ritardo un po pe rdare il tempo di visualizzare
                this.isLoadingDoc = false;
            }, 250);
        }
    }

    private resetFlags() {
        this.isAddingDoc = false;
        this.isEditingDoc = false;
        this.isDeletingDoc = false;
        this.isOnError = false;
        this.isLoadingDoc = false;
        this.isConfirmedOp = false;
    }

    private deleteDocument() {
        this._isOnServerOp.next(true);
        this.deleteDocSubscription = this.externalDocsService.deleteDocument(this._selectedDoc, this._selectedDocType == ExternalDocFamilyType.Patient)
            .pipe(finalize(() => this._isOnServerOp.next(false)))
            .subscribe({
                // Success
                next: () => {
                    this.notificationService.success(this.translate.instant('ExternalDocDeleteSuccess'));

                    // Rimuovo dalla tree
                    this.externalDocuments.forEach((e) => {
                        if (e.type == this._selectedDocType as any) {
                            e.linkedExternalDocuments.forEach((led, index) => {
                                if (led.id == this._selectedDoc.id)
                                    e.linkedExternalDocuments.splice(index, 1);
                            });
                        }
                    });

                    // Rimuovo la selezione
                    this._selectedDocType = null;
                    this.store.dispatch(seSetSelectExternalDoc({
                        doc: null,
                    }));

                    this.messageBoxText = this.translate.instant('ExternalDocDeleteSuccess');
                    this.isConfirmedOp = true;
                },
                // Error
                error: (error) => {
                    // Notifica errore;
                    let title = this.translate.instant('Exception_ExternalDocsFault');
                    let defaultMessage = this.translate.instant('ExternalDocDeleteFail');
                    this.notificationService.httpEstensaFaultError(error, title, defaultMessage);
                    this.logger.error(error);

                    this.messageBoxText = defaultMessage;
                    this.isOnError = true;
                },
            });
    }

    private updateDocument() {
        this._isOnServerOp.next(true);
        this.updateDocSubscription = this.externalDocsService.updateDocument(this._selectedDoc, this._selectedDocType == ExternalDocFamilyType.Patient)
            .pipe(finalize(() => {
                this._isOnServerOp.next(false);
            }))
            .subscribe({
                // Success
                next: () => {
                    // il flag dell'edit lo levo qui altrimenti si sovrappongono le messagebox!!
                    this.isEditingDoc = false;

                    this.notificationService.success(this.translate.instant('ExternalDocEditSuccess'));

                    this.updateDocumentInCollection(this._selectedDoc);

                    this.messageBoxText = this.translate.instant('ExternalDocEditSuccess');
                    this.isConfirmedOp = true;
                },
                // Error
                error: (error) => {
                    // il flag dell'edit lo levo qui altrimenti si sovrappongono le messagebox!!
                    this.isEditingDoc = false;

                    // Notifica errore;
                    let title = this.translate.instant('Exception_ExternalDocsFault');
                    let defaultMessage = this.translate.instant('ExternalDocEditFail');
                    this.notificationService.httpEstensaFaultError(error, title, defaultMessage);
                    this.logger.error(error);

                    this.messageBoxText = defaultMessage;
                    this.isOnError = true;
                },
            });
    }

    private addDocument() {
        this._isOnServerOp.next(true);
        this.addNewDocSubscription = this.externalDocsService.addExternaDoc(this._selectedDoc)
            .pipe(
                map(doc => doc.toJSON()),
                finalize(() => this._isOnServerOp.next(false)),
            )
            .subscribe({
                // Success
                next: (newDoc: ExternalDocument) => {
                    // il flag dell'add lo levo qui altrimenti si sovrappongono le messagebox!!
                    this.isAddingDoc = false;

                    this.notificationService.success(this.translate.instant('ExternalDocAddSuccess'));

                    this.externalDocuments.forEach((e) => {
                        // Se è di tipo paziente (c'è un solo livello paziente) aggiungo direttamente
                        // Se è a livello exam devo andare a prendere l'exam giusta
                        if (e.type == this._selectedDocType as any) {
                            if (e.type == ExternalDocFamilyType.Patient as any)
                                e.linkedExternalDocuments.unshift(newDoc);
                            else if (e.type == ExternalDocFamilyType.Study as any) { // Studio - aggiungo solo alla examination a cui sto legando
                                if (e.id == this._selectedDoc.guidExaminationList[0])
                                    e.linkedExternalDocuments.unshift(newDoc);
                            }
                        }
                    });

                    //var tmp = this._selectedDoc;
                    // Rimuovo la selezione....
                    this.store.dispatch(seSetSelectExternalDoc({
                        doc: null,
                    }));
                    /// ... e la rifaccio
                    this.store.dispatch(seSetSelectExternalDoc({
                        doc: newDoc, /*tmp*/
                    }));

                    this.messageBoxText = this.translate.instant('ExternalDocAddSuccess');
                    this.isConfirmedOp = true;
                },
                // Error
                error: (error) => {
                    // il flag dell'add lo levo qui altrimenti si sovrappongono le messagebox!!
                    this.isAddingDoc = false;

                    // Notifica errore;
                    let title = this.translate.instant('Exception_ExternalDocsFault');
                    let defaultMessage = this.translate.instant('ExternalDocAddFail');
                    this.notificationService.httpEstensaFaultError(error, title, defaultMessage);
                    this.logger.error(error);

                    this.messageBoxText = defaultMessage;
                    this.isOnError = true;
                },
            });
    }

    private getDocumentType(document: ExternalDocument): ExternalDocFamilyType {
        if (document) {
            let type = null;

            for (let i = 0; i < this.externalDocuments.length; i++) {
                const hiararchicalExternalDocument = this.externalDocuments[i];
                for (let n = 0; n < hiararchicalExternalDocument.linkedExternalDocuments.length; n++) {
                    const externalDoc = hiararchicalExternalDocument.linkedExternalDocuments[n];
                    if (externalDoc.id == document.id) {
                        type = hiararchicalExternalDocument.type;
                        return type;
                    }
                }
            }
        }

        return null;
    }

    private updateDocumentInCollection(document: ExternalDocument): boolean {
        for (let i = 0; i < this.externalDocuments.length; i++) {
            const hiararchicalExternalDocument = this.externalDocuments[i];
            for (let n = 0; n < hiararchicalExternalDocument.linkedExternalDocuments.length; n++) {
                const externalDoc = hiararchicalExternalDocument.linkedExternalDocuments[n];
                if (externalDoc.id == document.id) {
                    hiararchicalExternalDocument.linkedExternalDocuments[n] = document;
                    return true;
                }
            }
        }

        return false;
    }

    private showDialog(state: ExternalDocsState) {

        this.currentExternalDocSearchInfo = state.info;
        this.currentExternalDocPatientInfo = state.patientInfo;
        this.currentExternalDocExamInfo = state.examinationsInfo;

        // Apertura dialog
        this.seExternalDocsDialog = this.dialog.open(this.seExternalDocsDialogTemplate, {
            autoFocus: true,
            height: '95%',
            width: '90%',
            minWidth: '800px',
            minHeight: '600px',
            disableClose: true,
            panelClass: 'se-external-docs-dialog-container',
        });

        this.afterCloseEventSubscription = this.seExternalDocsDialog.afterClosed().subscribe(() => {
            this.afterCloseEventSubscription.unsubscribe();
            this.afterOpenEventSubscription.unsubscribe();

            this.loadSubscription.unsubscribe();

            if (this.updateDocSubscription)
                this.updateDocSubscription.unsubscribe();

            if (this.addNewDocSubscription)
                this.addNewDocSubscription.unsubscribe();

            if (this.deleteDocSubscription)
                this.deleteDocSubscription.unsubscribe();

            this.currentExternalDocSearchInfo = null;
            this.messageBoxText = '';

            this.resetFlags();

            this._selectedDoc = null;
            this._selectedDocType = null;
            this.pdfSource = null;
            this.imageSource = null;

            this.externalDocuments = [];
        });

        this.afterOpenEventSubscription = this.seExternalDocsDialog.afterOpened().subscribe(() => {
            this.loadExternalDocuments();
        });
    }

    private listenForStoreEvents() {
        this.actions
            .pipe(
                takeUntil(this.ngUnsubscribe),
                ofType(seOpenExternalDocsOnDialog),
            )
            .subscribe((value: ExternalDocsState) => {
                this._isWaiting.next(true);

                // Qui carico gli external
                this.showDialog(value);
            });

        this.actions
            .pipe(
                takeUntil(this.ngUnsubscribe),
                ofType(seSetSelectExternalDoc),
            )
            .subscribe(
                (value) => {
                    // E' cambiata la selezione
                    if (value.doc == null
                        || !this._selectedDoc
                        || (this._selectedDoc && value.doc.id != this._selectedDoc.id)) {
                        this._selectedDoc = value.doc;

                        if (this._selectedDoc) { // se è nullo non faccio nulla
                            this.isLoadingDoc = true;
                            this.pdfSource = null;

                            this.loadSelectedDoc();
                        }
                    }
                });

        this.actions
            .pipe(
                takeUntil(this.ngUnsubscribe),
                ofType(seUpdateSelectExternalDoc),
            ).subscribe((value) => {
            this._selectedDoc = value.doc;
            this.confirmOps('modify');
        });

        this.actions
            .pipe(
                takeUntil(this.ngUnsubscribe),
                ofType(seAddNewExternalDoc))
            .subscribe((value) => {
                this._selectedDoc = value.doc;
                this.confirmOps('adding');
            });
    }
}
